import { authFetch } from "@/lib/authFetch";
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Menu, Inbox, Check, X, Clock } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccessRequests = () => {
  const { isDarkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [pendingCount, setPendingCount] = useState(0);
  const [actionFor, setActionFor] = useState({}); // request_id -> chosen action
  const [busy, setBusy] = useState(null);

  const fetchRequests = async (status) => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/admin/access-requests?status=${status}`, { credentials: "include" });
      if (resp.ok) {
        const data = await resp.json();
        setRequests(data.requests || []);
        setPendingCount(data.pending_count || 0);
      }
    } catch (e) {
      toast.error("Could not load access requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(statusFilter); }, [statusFilter]);

  const approve = async (req) => {
    const action = actionFor[req.request_id] || req.request_type || "co_referral";
    setBusy(req.request_id);
    try {
      const resp = await authFetch(`${API}/admin/access-requests/${req.request_id}/approve`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        toast.success(`Approved (${action.replace("_", " ")}).`);
        fetchRequests(statusFilter);
      } else {
        toast.error(typeof data.detail === "string" ? data.detail : "Could not approve.");
      }
    } finally {
      setBusy(null);
    }
  };

  const deny = async (req) => {
    setBusy(req.request_id);
    try {
      const resp = await authFetch(`${API}/admin/access-requests/${req.request_id}/deny`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "" }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        toast.success("Request denied.");
        fetchRequests(statusFilter);
      } else {
        toast.error(typeof data.detail === "string" ? data.detail : "Could not deny.");
      }
    } finally {
      setBusy(null);
    }
  };

  const statusBadge = (s) => {
    const map = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", denied: "bg-red-100 text-red-700" };
    return <Badge className={map[s] || "bg-slate-100 text-slate-700"}>{s}</Badge>;
  };

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={true} />

      <main className="flex-1 min-w-0 md:ml-64">
        <header className={`sticky top-0 z-30 px-4 md:px-6 py-4 border-b transition-colors duration-300 ${isDarkMode ? "bg-black border-[#1a3d18]" : "header-glass"}`}>
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className={`md:hidden p-2 rounded-lg ${isDarkMode ? "text-white" : ""}`}>
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <h1 className={`text-xl md:text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                Client Access Requests
              </h1>
              <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                Approve co-referrals or ownership transfers requested by partners
              </p>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-4">
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList data-testid="access-request-tabs">
              <TabsTrigger value="pending" data-testid="tab-pending">
                Pending {pendingCount > 0 && <span className="ml-1 text-xs bg-amber-500 text-white rounded-full px-1.5">{pendingCount}</span>}
              </TabsTrigger>
              <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
              <TabsTrigger value="denied" data-testid="tab-denied">Denied</TabsTrigger>
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            </TabsList>
          </Tabs>

          {loading ? (
            <p className={`${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Loading...</p>
          ) : requests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Inbox className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No {statusFilter === "all" ? "" : statusFilter} access requests.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <Card key={req.request_id} data-testid={`access-request-${req.request_id}`}>
                  <CardContent className="py-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{req.requester_name}</span>
                          <span className="text-sm text-slate-500">({req.requester_email})</span>
                          {statusBadge(req.status)}
                          <Badge variant="outline">{(req.request_type || "").replace("_", " ")}</Badge>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          Wants access to <span className="font-medium">{req.client_name}</span>
                          {req.current_affiliate_1_name ? <> — currently with <span className="font-medium">{req.current_affiliate_1_name}</span></> : null}
                        </p>
                        {req.reason ? <p className="text-sm text-slate-500 mt-1 italic">"{req.reason}"</p> : null}
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {new Date(req.created_at).toLocaleString()}
                          {req.decided_by ? ` · decided by ${req.decided_by}` : ""}
                        </p>
                      </div>

                      {req.status === "pending" && (
                        <div className="flex items-center gap-2 shrink-0">
                          <Select
                            value={actionFor[req.request_id] || req.request_type || "co_referral"}
                            onValueChange={(v) => setActionFor((p) => ({ ...p, [req.request_id]: v }))}
                          >
                            <SelectTrigger className="w-40" data-testid={`action-select-${req.request_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="co_referral">Co-referral (add)</SelectItem>
                              <SelectItem value="transfer">Transfer ownership</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button size="sm" onClick={() => approve(req)} disabled={busy === req.request_id} data-testid={`approve-${req.request_id}`}>
                            <Check className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deny(req)} disabled={busy === req.request_id} data-testid={`deny-${req.request_id}`}>
                            <X className="h-4 w-4 mr-1" /> Deny
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AccessRequests;
